import React from 'react';
import type {
  ControllerProps,
  FieldValues,
  Path,
  UseControllerProps,
  UseControllerReturn,
  FieldPath,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

export type MapControllerToInputProps<
  TInputProps,
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>
> = (
  innerRenderProps: UseControllerReturn<TFieldValues, TName>,
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render'>,
  defaultInputProps?: Partial<TInputProps>
) => TInputProps;

const mergeControllerProps = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  name: TName,
  defaultProps: Omit<UseControllerProps<TFieldValues, TName>, 'name'> | undefined,
  newProps?: Omit<UseControllerProps<TFieldValues, TName>, 'name'>
): UseControllerProps<TFieldValues, TName> => ({
  name,
  ...defaultProps,
  ...newProps,
  rules: { ...defaultProps?.rules, ...newProps?.rules },
});

export const makeControlledInput =
  <TFieldValues extends FieldValues = FieldValues>() =>
  <
    TDefaultControllerProps extends Partial<UseControllerProps<TFieldValues, TName>>,
    TInputProps extends {},
    TDefaultInputProps extends TInputProps,
    TName extends Path<TFieldValues>
  >({
    defaultControllerProps,
    defaultInputProps,
    name,
    propsMapper,
    Component,
  }: {
    defaultControllerProps?: TDefaultControllerProps;
    defaultInputProps?: TDefaultInputProps;
    name: TName;
    propsMapper: MapControllerToInputProps<TInputProps, TFieldValues, TName>;
    Component: (props: TInputProps) => JSX.Element;
  }) =>
  ({
    controllerProps,
    ...inputProps
  }: Partial<TInputProps> & {
    controllerProps?: Omit<UseControllerProps<TFieldValues, TName>, 'name'>;
  }): JSX.Element => {
    const mergedControllerProps = mergeControllerProps(
      name,
      defaultControllerProps,
      controllerProps
    );

    return (
      <Controller<TFieldValues, TName>
        render={(innerRenderProps): React.ReactElement => {
          const mergedInputProps = {
            ...defaultInputProps,
            ...inputProps,
            ...propsMapper(innerRenderProps, mergedControllerProps, defaultInputProps),
          };
          return <Component {...mergedInputProps} />;
        }}
        {...mergedControllerProps}
      />
    );
  };
